import { useState } from "react";
import { MUTATIONS, useMutation } from "../../../api";
import type { WorkshopId, WorkshopParticipant } from "../../../api/workshop";
import type { SmallTeamId } from "../../../api/smallTeam";
import type { UserId } from "../../../api/user";
import UserDisplay from "../../../design-system/primitives/components/UserDisplay/UserDisplay";
import IconButton from "../../../design-system/primitives/components/IconButton/IconButton";
import Tooltip from "../../../design-system/primitives/components/Tooltip/Tooltip";
import Dialog from "../../../design-system/primitives/components/Dialog/Dialog";
import Button from "../../../design-system/primitives/components/Button/Button";
import CopyToClipboard from "../../../design-system/primitives/components/CopyToClipboard/CopyToClipboard";
import DropdownMenu from "../../../design-system/primitives/components/DropdownMenu/DropdownMenu";
import Text from "../../../design-system/primitives/components/Text/Text";
import { useRemoveFromWorkshop } from "./useRemoveFromWorkshop";
import { useStudentDragSource, type Group } from "./groups";

const RESET_LINK_ERROR =
  "Der Link konnte nicht erstellt werden. Bitte versuche es erneut.";

/**
 * Drives the "reset password" dialog: opening it kicks off a fresh link,
 * landing in either `link` or `error` once the request settles.
 */
const useResetPasswordLink = (userId: UserId) => {
  const [isOpen, setIsOpen] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [doCreateResetPasswordLink] = useMutation(
    MUTATIONS.createResetPasswordLink,
  );

  const open = () => {
    setLink(null);
    setError(null);
    setIsOpen(true);
    doCreateResetPasswordLink({ userId })
      ?.then((result) => setLink(result.url))
      .catch(() => setError(RESET_LINK_ERROR));
  };

  return { isOpen, setIsOpen, link, error, open };
};

type MoveToGroupMenuProps = {
  studentName: string;
  currentGroupId: SmallTeamId | null;
  groups?: ReadonlyArray<Group>;
  onMoveToGroup?: (groupId: SmallTeamId | null) => void;
};

/**
 * The keyboard-operable alternative to dragging a student into a group — the
 * Figma redesign doesn't have one, but drag-only assignment would be an
 * accessibility gap. Renders nothing when there's nowhere to move to.
 */
const MoveToGroupMenu = ({
  studentName,
  currentGroupId,
  groups,
  onMoveToGroup,
}: MoveToGroupMenuProps) => {
  const moveTargets = (groups ?? []).filter(
    (group) => group.id !== currentGroupId,
  );
  const canMoveBackToList = currentGroupId != null;

  if (!onMoveToGroup || (moveTargets.length === 0 && !canMoveBackToList)) {
    return null;
  }

  return (
    <DropdownMenu
      triggerButtonVariant="secondary"
      triggerIcon="menu"
      triggerAriaLabel={`${studentName} in eine andere Gruppe verschieben`}
    >
      {moveTargets.map((group) => (
        <DropdownMenu.Item
          key={group.id}
          onSelect={() => onMoveToGroup(group.id)}
        >
          {`Nach "${group.name}" verschieben`}
        </DropdownMenu.Item>
      ))}
      {canMoveBackToList && (
        <DropdownMenu.Item onSelect={() => onMoveToGroup(null)}>
          Zurück in die Einladeliste
        </DropdownMenu.Item>
      )}
    </DropdownMenu>
  );
};

type StudentRowProps = {
  student: WorkshopParticipant;
  workshop: { id: WorkshopId };
  surface: "base" | "nested";
  /** Other groups the student can be moved to — omit the menu entirely when there's nothing to move to. */
  groups?: ReadonlyArray<Group>;
  onMoveToGroup?: (groupId: SmallTeamId | null) => void;
};

/**
 * One student's row, wired to the real workshop mutations: reset password
 * (opens a Dialog with a fresh link) and remove from workshop (an
 * AlertDialog). Drag-and-drop into a group and its keyboard-operable menu
 * fallback live in `useStudentDragSource`/`MoveToGroupMenu`.
 */
const StudentRow = ({
  student,
  workshop,
  surface,
  groups,
  onMoveToGroup,
}: StudentRowProps) => {
  const { drag, isDragging } = useStudentDragSource(student);
  const resetPasswordLink = useResetPasswordLink(student.id);
  const removeFromWorkshop = useRemoveFromWorkshop(workshop.id, student);

  return (
    <>
      <UserDisplay
        user={student}
        surface={surface}
        dragRef={drag}
        isDragging={isDragging}
        actionsSlot={
          <>
            <MoveToGroupMenu
              studentName={student.name}
              currentGroupId={student.smallTeamId}
              groups={groups}
              onMoveToGroup={onMoveToGroup}
            />

            <Tooltip content="Passwort zurücksetzen">
              <IconButton
                icon="key"
                ariaLabel={`Passwort für ${student.name} zurücksetzen`}
                variant="secondary"
                onClick={resetPasswordLink.open}
              />
            </Tooltip>

            <Tooltip content="Entfernen">
              <IconButton
                icon="x"
                ariaLabel={`${student.name} entfernen`}
                variant="secondary"
                onClick={removeFromWorkshop.open}
              />
            </Tooltip>
          </>
        }
      />

      <Dialog
        open={resetPasswordLink.isOpen}
        onOpenChange={resetPasswordLink.setIsOpen}
        title={`Passwort von "${student.name}" zurücksetzen`}
        description={
          resetPasswordLink.error ??
          "Kopiere den Link und schicke ihn an die entsprechende Person, damit sie ein neues Passwort einstellen kann."
        }
        closeAriaLabel="Schließen"
        contentSlot={
          resetPasswordLink.error ? undefined : resetPasswordLink.link ? (
            <CopyToClipboard
              value={resetPasswordLink.link}
              label="Link kopieren"
            />
          ) : (
            <Text variant="body" color="neutral-100">
              Link wird erstellt …
            </Text>
          )
        }
        footer={
          <Button
            title="Schließen"
            color="accent"
            size="large"
            fullWidth
            onClick={() => resetPasswordLink.setIsOpen(false)}
          />
        }
      />

      {removeFromWorkshop.dialogElement}
    </>
  );
};

export default StudentRow;
